import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useNavigate,
  useOutletContext,
} from 'react-router-dom';

import {
  BarChart3,
  Bell,
  Building2,
  Clock3,
  CreditCard,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  RefreshCw,
  Send,
  Settings,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  Wallet,
  X,
  Eye,
  MapPin,
  Store,
  CircleCheck,
  CircleAlert,
  TrendingUp,
} from 'lucide-react';

import './ControlCenterVendors.css';
import './ControlCenterSmartDigest.css';


const ControlCenterSmartDigest = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext();


  // ============================================================
  // ADMIN
  // ============================================================

  let savedAdmin = null;

  try {
    const rawAdmin =
      localStorage.getItem(
        'vakra_control_admin'
      ) ||
      sessionStorage.getItem(
        'vakra_control_admin'
      );

    if (rawAdmin) {
      savedAdmin =
        JSON.parse(rawAdmin);
    }
  } catch (error) {
    console.error(
      'Saved admin parse error:',
      error
    );
  }

  const admin =
    outletContext?.admin ||
    savedAdmin ||
    null;


  // ============================================================
  // TOKEN
  // ============================================================

  const getToken = () => {
    return (
      localStorage.getItem(
        'vakra_control_admin_token'
      ) ||
      sessionStorage.getItem(
        'vakra_control_admin_token'
      ) ||
      ''
    );
  };


  // ============================================================
  // STATES
  // ============================================================

  const [sidebarOpen, setSidebarOpen] =
    useState(false);

  const [
    buildingDigest,
    setBuildingDigest,
  ] = useState(false);

  const [
    loadingData,
    setLoadingData,
  ] = useState(true);

  const [
    pageError,
    setPageError,
  ] = useState('');

  const [
    queueingDigest,
    setQueueingDigest,
  ] = useState(false);

  const [
    queueStatusMessage,
    setQueueStatusMessage,
  ] = useState('');


  // ============================================================
  // REAL OVERVIEW DATA
  // ============================================================

  const [
    overview,
    setOverview,
  ] = useState({
    activeCampaigns: 0,
    totalCustomers: 0,
    eligibleCustomers: 0,
    activeOffers: 0,
    matchingVendors: 0,
    todayBatch: 0,
  });


  // ============================================================
  // REAL DIGEST DATA
  // ============================================================

  const [
    digestData,
    setDigestData,
  ] = useState({
    digestId: null,

    campaignName:
      'No digest built yet',

    microZone:
      'All Areas',

    radius:
      '3 KM',

    eligibleCustomers: 0,

    selectedCustomers: 0,

    matchingVendors: 0,

    selectedOffers: 0,

    status:
      'inactive',

    createdAt:
      null,
  });


  // ============================================================
  // REAL CAMPAIGNS
  // ============================================================

  const [
    campaigns,
    setCampaigns,
  ] = useState([]);


  // ============================================================
  // REAL VENDOR EXPOSURE
  // ============================================================

  const [
    vendorExposure,
    setVendorExposure,
  ] = useState([]);


  // ============================================================
  // REAL AUDIENCE
  // ============================================================

  const [
    audience,
    setAudience,
  ] = useState({
    totalPool: 0,
    eligible: 0,

    /*
      Personalized backend se real audience counters:
      cooldown / push consent / no-match.
    */

    cooldownBlocked: 0,
    pushDisabled: 0,
    noMatch: 0,
  });


  // ============================================================
  // REAL DETAIL DATA
  // ============================================================

  const [eligibleCustomers, setEligibleCustomers] = useState([]);
  const [matchingVendors, setMatchingVendors] = useState([]);
  const [vendorOffers, setVendorOffers] = useState([]);
  const [vendorOfferSummary, setVendorOfferSummary] = useState({
    activeOfferVendors: 0,
    totalActiveOffers: 0,
    liveNowOffers: 0,
    typeCounts: {},
  });

  const [qrAnalytics, setQrAnalytics] = useState({
    summary: {
      totalQrOrders: 0,
      uniqueIdentifiedCustomers: 0,
      anonymousQrOrders: 0,
      qrVendors: 0,
      todayQrOrders: 0,
    },
    vendors: [],
    recentOrders: [],
  });

  const [platformAnalytics, setPlatformAnalytics] = useState({
    summary: {
      totalCustomers: 0,
      newCustomersToday: 0,
      newCustomersThisWeek: 0,
      newCustomersThisMonth: 0,
      repeatCustomers: 0,
      totalOrders: 0,
      anonymousOrders: 0,
      totalOrderingVendors: 0,
      ordersToday: 0,
      ordersThisWeek: 0,
      ordersThisMonth: 0,
      totalSales: 0,
      salesToday: 0,
    },
    sources: [],
    orderModes: [],
    payments: [],
    orderStatuses: [],
    vendors: [],
    recentNewCustomers: [],
    recentOrders: [],
  });

  /*
    Ek time par sirf ek detail section open hoga.
    Isse page unnecessarily lamba nahi hoga.
  */
  const [openDetailPanel, setOpenDetailPanel] = useState('');

  const showEligibleCustomers = openDetailPanel === 'eligible-customers';
  const showMatchingVendors = openDetailPanel === 'matching-vendors';
  const showVendorOffers = openDetailPanel === 'vendor-offers';
  const showQrAnalytics = openDetailPanel === 'qr-analytics';
  const showPlatformAnalytics = openDetailPanel === 'platform-analytics';

  const toggleDetailPanel = (panelName) => {
    setOpenDetailPanel((current) =>
      current === panelName ? '' : panelName
    );
  };


  // ============================================================
  // MENU
  // ============================================================

  const menuItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path:
        '/control-center/dashboard',
    },

    {
      label: 'Vendors',
      icon: Building2,
      path:
        '/control-center/vendors',
    },

    {
      label: 'Subscriptions',
      icon: CreditCard,
      path:
        '/control-center/subscriptions',
    },

    {
      label: 'Payments',
      icon: Wallet,
      path:
        '/control-center/payments',
    },

    {
      label: 'Agreements',
      icon: FileText,
      path:
        '/control-center/agreements',
    },

    {
      label:
        'Smart Offer Digest',
      icon: Sparkles,
      path:
        '/control-center/smart-digest',
      active: true,
    },

    {
      label: 'Notifications',
      icon: Bell,
      path:
        '/control-center/notifications',
    },

    {
      label: 'Analytics',
      icon: BarChart3,
      path:
        '/control-center/analytics',
    },

    {
      label: 'Support',
      icon: MessageSquare,
      path:
        '/control-center/support',
    },

    {
      label: 'Settings',
      icon: Settings,
      path:
        '/control-center/settings',
    },
  ];


  // ============================================================
  // FORMAT DATE
  // ============================================================

  const formatDateTime = (
    value
  ) => {
    if (!value) {
      return '-';
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return String(value);
    }

    return date.toLocaleString(
      'en-IN',
      {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }
    );
  };


  // ============================================================
  // NORMALIZE DIGEST
  // ============================================================

  const normalizeLatestDigest = (
    latestDigest
  ) => {
    if (!latestDigest) {
      return {
        digestId: null,

        campaignName:
          'No digest built yet',

        microZone:
          'All Areas',

        radius:
          '3 KM',

        eligibleCustomers: 0,

        selectedCustomers: 0,

        matchingVendors: 0,

        selectedOffers: 0,

        status:
          'inactive',

        createdAt:
          null,
      };
    }

    return {
      digestId:
        latestDigest.digestId ||
        latestDigest.id ||
        null,

      campaignName:
        latestDigest.campaignName ||
        latestDigest.campaign_name ||
        'Smart Food Offers',

      microZone:
        latestDigest.microZone ||
        latestDigest.micro_zone ||
        'All Areas',

      radius:
        `${
          Number(
            latestDigest.radiusKm ||
            latestDigest.radius_km ||
            3
          )
        } KM`,

      eligibleCustomers:
        Number(
          latestDigest
            .eligibleCustomers ||
          latestDigest
            .eligible_customers ||
          0
        ),

      selectedCustomers:
        Number(
          latestDigest
            .selectedCustomers ||
          latestDigest
            .selected_customers ||
          0
        ),

      matchingVendors:
        Number(
          latestDigest
            .matchingVendors ||
          latestDigest
            .matching_vendors ||
          0
        ),

      selectedOffers:
        Number(
          latestDigest
            .selectedOffers ||
          latestDigest
            .selected_offers ||
          0
        ),

      status:
        latestDigest.status ||
        'inactive',

      createdAt:
        latestDigest.createdAt ||
        latestDigest.created_at ||
        null,
    };
  };


  // ============================================================
  // AUTH ERROR
  // ============================================================

  const handleAuthFailure = () => {
    localStorage.removeItem(
      'vakra_control_admin_token'
    );

    localStorage.removeItem(
      'vakra_control_admin'
    );

    sessionStorage.removeItem(
      'vakra_control_admin_token'
    );

    sessionStorage.removeItem(
      'vakra_control_admin'
    );

    navigate(
      '/control-center/login',
      {
        replace: true,
      }
    );
  };


  // ============================================================
  // LOAD OVERVIEW
  // ============================================================

  const loadOverview =
    async () => {
      const token =
        getToken();

      if (!token) {
        handleAuthFailure();
        return;
      }

      const response =
        await fetch(
          `${window.location.origin}/api/control-center/smart-digest/overview`,
          {
            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );

      const data =
        await response.json();

      if (
        response.status === 401 ||
        response.status === 403
      ) {
        handleAuthFailure();
        return;
      }

      if (
        !response.ok ||
        !data?.success
      ) {
        throw new Error(
          data?.details ||
          data?.error ||
          'Overview load nahi hua.'
        );
      }

      const realOverview =
        data.overview || {};

      setOverview({
        activeCampaigns:
          Number(
            realOverview
              .activeCampaigns ||
            0
          ),

        totalCustomers:
          Number(
            realOverview
              .totalCustomers ||
            0
          ),

        eligibleCustomers:
          Number(
            realOverview
              .eligibleCustomers ||
            0
          ),

        activeOffers:
          Number(
            realOverview
              .activeOffers ||
            0
          ),

        matchingVendors:
          Number(
            realOverview
              .matchingVendors ||
            0
          ),

        todayBatch:
          Number(
            realOverview
              .todayBatch ||
            0
          ),
      });

      const realAudience =
        data.audience || {};

      setAudience({
        totalPool:
          Number(
            realAudience
              .totalPool ||
            0
          ),

        eligible:
          Number(
            realAudience
              .eligible ||
            0
          ),

        cooldownBlocked:
          Number(
            realAudience
              .cooldownBlocked ||
            0
          ),

        pushDisabled:
          Number(
            realAudience
              .pushDisabled ||
            0
          ),

        noMatch:
          Number(
            realAudience
              .noMatch ||
            0
          ),
      });

      setDigestData(
        normalizeLatestDigest(
          data.latestDigest
        )
      );
    };


  // ============================================================
  // LOAD CAMPAIGNS
  // ============================================================

  const loadCampaigns =
    async () => {
      const token =
        getToken();

      if (!token) {
        return;
      }

      const response =
        await fetch(
          `${window.location.origin}/api/control-center/smart-digest/campaigns`,
          {
            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );

      const data =
        await response.json();

      if (
        response.status === 401 ||
        response.status === 403
      ) {
        handleAuthFailure();
        return;
      }

      if (
        !response.ok ||
        !data?.success
      ) {
        throw new Error(
          data?.details ||
          data?.error ||
          'Campaigns load nahi hue.'
        );
      }

      setCampaigns(
        Array.isArray(
          data.campaigns
        )
          ? data.campaigns
          : []
      );
    };


  // ============================================================
  // LOAD VENDOR EXPOSURE
  // ============================================================

  const loadVendorExposure =
    async () => {
      const token =
        getToken();

      if (!token) {
        return;
      }

      const response =
        await fetch(
          `${window.location.origin}/api/control-center/smart-digest/reports/vendor-exposure`,
          {
            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );

      const data =
        await response.json();

      if (
        response.status === 401 ||
        response.status === 403
      ) {
        handleAuthFailure();
        return;
      }

      if (
        !response.ok ||
        !data?.success
      ) {
        throw new Error(
          data?.details ||
          data?.error ||
          'Vendor exposure load nahi hua.'
        );
      }

      setVendorExposure(
        Array.isArray(
          data.vendors
        )
          ? data.vendors
          : []
      );
    };


  // ============================================================
  // LOAD ELIGIBLE CUSTOMERS
  // ============================================================

  const loadEligibleCustomers = async () => {
    const token = getToken();
    if (!token) return;

    const response = await fetch(
      `${window.location.origin}/api/control-center/smart-digest/eligible-customers?radius=3&offerLimit=10`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json();

    if (response.status === 401 || response.status === 403) {
      handleAuthFailure();
      return;
    }

    if (!response.ok || !data?.success) {
      throw new Error(
        data?.details ||
        data?.error ||
        'Eligible customers load nahi hue.'
      );
    }

    setEligibleCustomers(
      Array.isArray(data.customers) ? data.customers : []
    );
  };


  // ============================================================
  // LOAD MATCHING VENDORS
  // ============================================================

  const loadMatchingVendors = async () => {
    const token = getToken();
    if (!token) return;

    const response = await fetch(
      `${window.location.origin}/api/control-center/smart-digest/matching-vendors?radius=3&offerLimit=10`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json();

    if (response.status === 401 || response.status === 403) {
      handleAuthFailure();
      return;
    }

    if (!response.ok || !data?.success) {
      throw new Error(
        data?.details ||
        data?.error ||
        'Matching vendors load nahi hue.'
      );
    }

    setMatchingVendors(
      Array.isArray(data.vendors) ? data.vendors : []
    );
  };


  // ============================================================
  // LOAD LIVE VENDOR OFFERS
  // ============================================================

  const loadVendorOffers = async () => {
    const token = getToken();
    if (!token) return;

    const response = await fetch(
      `${window.location.origin}/api/control-center/smart-digest/vendor-offers`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json();

    if (response.status === 401 || response.status === 403) {
      handleAuthFailure();
      return;
    }

    if (!response.ok || !data?.success) {
      throw new Error(
        data?.details ||
        data?.error ||
        'Vendor offers load nahi hue.'
      );
    }

    setVendorOfferSummary({
      activeOfferVendors: Number(data?.summary?.activeOfferVendors || 0),
      totalActiveOffers: Number(data?.summary?.totalActiveOffers || 0),
      liveNowOffers: Number(data?.summary?.liveNowOffers || 0),
      typeCounts: data?.summary?.typeCounts || {},
    });

    setVendorOffers(
      Array.isArray(data.vendors) ? data.vendors : []
    );
  };


  // ============================================================
  // LOAD QR ANALYTICS
  // ============================================================

  const loadQrAnalytics = async () => {
    const token = getToken();
    if (!token) return;

    const response = await fetch(
      `${window.location.origin}/api/control-center/smart-digest/qr-analytics`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json();

    if (response.status === 401 || response.status === 403) {
      handleAuthFailure();
      return;
    }

    if (!response.ok || !data?.success) {
      throw new Error(
        data?.details ||
        data?.error ||
        'QR analytics load nahi hua.'
      );
    }

    setQrAnalytics({
      summary: {
        totalQrOrders: Number(data?.summary?.totalQrOrders || 0),
        uniqueIdentifiedCustomers: Number(
          data?.summary?.uniqueIdentifiedCustomers || 0
        ),
        anonymousQrOrders: Number(data?.summary?.anonymousQrOrders || 0),
        qrVendors: Number(data?.summary?.qrVendors || 0),
        todayQrOrders: Number(data?.summary?.todayQrOrders || 0),
      },
      vendors: Array.isArray(data.vendors) ? data.vendors : [],
      recentOrders: Array.isArray(data.recentOrders)
        ? data.recentOrders
        : [],
    });
  };


  // ============================================================
  // LOAD PLATFORM CUSTOMER ANALYTICS
  // ============================================================

  const loadPlatformAnalytics = async () => {
    const token = getToken();
    if (!token) return;

    const response = await fetch(
      `${window.location.origin}/api/control-center/smart-digest/platform-analytics`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json();

    if (response.status === 401 || response.status === 403) {
      handleAuthFailure();
      return;
    }

    if (!response.ok || !data?.success) {
      throw new Error(
        data?.details ||
        data?.error ||
        'Platform analytics load nahi hua.'
      );
    }

    setPlatformAnalytics({
      summary: {
        totalCustomers: Number(data?.summary?.totalCustomers || 0),
        newCustomersToday: Number(data?.summary?.newCustomersToday || 0),
        newCustomersThisWeek: Number(data?.summary?.newCustomersThisWeek || 0),
        newCustomersThisMonth: Number(data?.summary?.newCustomersThisMonth || 0),
        repeatCustomers: Number(data?.summary?.repeatCustomers || 0),
        totalOrders: Number(data?.summary?.totalOrders || 0),
        anonymousOrders: Number(data?.summary?.anonymousOrders || 0),
        totalOrderingVendors: Number(data?.summary?.totalOrderingVendors || 0),
        ordersToday: Number(data?.summary?.ordersToday || 0),
        ordersThisWeek: Number(data?.summary?.ordersThisWeek || 0),
        ordersThisMonth: Number(data?.summary?.ordersThisMonth || 0),
        totalSales: Number(data?.summary?.totalSales || 0),
        salesToday: Number(data?.summary?.salesToday || 0),
      },
      sources: Array.isArray(data.sources) ? data.sources : [],
      orderModes: Array.isArray(data.orderModes) ? data.orderModes : [],
      payments: Array.isArray(data.payments) ? data.payments : [],
      orderStatuses: Array.isArray(data.orderStatuses) ? data.orderStatuses : [],
      vendors: Array.isArray(data.vendors) ? data.vendors : [],
      recentNewCustomers: Array.isArray(data.recentNewCustomers)
        ? data.recentNewCustomers
        : [],
      recentOrders: Array.isArray(data.recentOrders)
        ? data.recentOrders
        : [],
    });
  };


  // ============================================================
  // LOAD ALL REAL DATA
  // ============================================================

  const loadSmartDigestData =
    async () => {
      try {
        setLoadingData(true);
        setPageError('');

        await Promise.all([
          loadOverview(),
          loadCampaigns(),
          loadVendorExposure(),
          loadEligibleCustomers(),
          loadMatchingVendors(),
          loadVendorOffers(),
          loadQrAnalytics(),
          loadPlatformAnalytics(),
        ]);

      } catch (error) {
        console.error(
          'Smart Digest data load error:',
          error
        );

        setPageError(
          error.message ||
          'Smart Digest data load nahi hua.'
        );

      } finally {
        setLoadingData(false);
      }
    };


  // ============================================================
  // PAGE LOAD
  // ============================================================

  useEffect(() => {
    loadSmartDigestData();

  }, []);


  // ============================================================
  // REAL TOTALS
  // ============================================================

  const totals =
    useMemo(() => {
      return {
        campaigns:
          overview.activeCampaigns,

        eligibleCustomers:
          overview.eligibleCustomers,

        matchingVendors:
          overview.matchingVendors,

        todayBatch:
          overview.todayBatch,
      };
    }, [overview]);


  // ============================================================
  // MENU CLICK
  // ============================================================

  const handleMenuClick = (
    item
  ) => {
    setSidebarOpen(false);

    const enabledRoutes = [
      '/control-center/dashboard',
      '/control-center/vendors',
      '/control-center/subscriptions',
      '/control-center/payments',
      '/control-center/agreements',
      '/control-center/smart-digest',
    ];

    if (
      enabledRoutes.includes(
        item.path
      )
    ) {
      navigate(item.path);
      return;
    }

    alert(
      `${item.label} page next phase me banega.`
    );
  };


  // ============================================================
  // LOGOUT
  // ============================================================

  const handleLogout =
    async () => {
      const token =
        getToken();

      try {
        if (token) {
          await fetch(
            `${window.location.origin}/api/control-center/logout`,
            {
              method:
                'POST',

              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );
        }
      } catch (error) {
        console.error(
          'Logout error:',
          error
        );
      }

      localStorage.removeItem(
        'vakra_control_admin_token'
      );

      localStorage.removeItem(
        'vakra_control_admin'
      );

      sessionStorage.removeItem(
        'vakra_control_admin_token'
      );

      sessionStorage.removeItem(
        'vakra_control_admin'
      );

      navigate(
        '/control-center/login',
        {
          replace: true,
        }
      );
    };


  // ============================================================
  // REAL BUILD DIGEST
  // ============================================================

  const handleBuildDigest =
    async () => {
      const token =
        getToken();

      if (!token) {
        handleAuthFailure();
        return;
      }

      try {
        setBuildingDigest(true);
        setPageError('');

        const response =
          await fetch(
            `${window.location.origin}/api/control-center/smart-digest/build`,
            {
              method:
                'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`,
              },

              /*
                Abhi default rules:

                Radius = 3 KM
                Batch = 40
                Offer Limit = 10

                Backend me bhi same
                defaults hain.
              */

              body:
                JSON.stringify({
                  campaignName:
                    'Smart Food Offers',

                  microZone:
                    '',

                  radiusKm:
                    3,

                  batchSize:
                    40,

                  offerLimit:
                    10,
                }),
            }
          );

        const data =
          await response.json();

        if (
          response.status === 401 ||
          response.status === 403
        ) {
          handleAuthFailure();
          return;
        }

        if (
          !response.ok ||
          !data?.success
        ) {
          throw new Error(
            data?.details ||
            data?.error ||
            'Digest build nahi hua.'
          );
        }

        if (data.digest) {
          setDigestData({
            digestId:
              data.digest.digestId,

            campaignName:
              data.digest
                .campaignName ||
              'Smart Food Offers',

            microZone:
              data.digest
                .microZone ||
              'All Areas',

            radius:
              `${
                Number(
                  data.digest
                    .radiusKm ||
                  3
                )
              } KM`,

            eligibleCustomers:
              Number(
                data.digest
                  .eligibleCustomers ||
                0
              ),

            selectedCustomers:
              Number(
                data.digest
                  .selectedCustomers ||
                0
              ),

            matchingVendors:
              Number(
                data.digest
                  .matchingVendors ||
                0
              ),

            selectedOffers:
              Number(
                data.digest
                  .selectedOffers ||
                0
              ),

            status:
              data.digest.status ||
              'ready',

            createdAt:
              new Date()
                .toISOString(),
          });
        }

        /*
          Build complete hone ke
          baad saara real data
          dobara load karo.
        */

        await loadSmartDigestData();

        alert(
          'Smart Offer Digest real database data ke saath build ho gaya.'
        );

      } catch (error) {
        console.error(
          'Build digest error:',
          error
        );

        setPageError(
          error.message ||
          'Digest build nahi hua.'
        );

        alert(
          error.message ||
          'Digest build nahi hua.'
        );

      } finally {
        setBuildingDigest(false);
      }
    };


  // ============================================================
  // NOTIFICATION QUEUE
  // ============================================================

  const handleSendToNotificationQueue =
    async () => {
      if (
        !digestData.digestId ||
        digestData.status !==
          'ready'
      ) {
        alert(
          digestData.status === 'queued' ||
          digestData.status === 'scheduled'
            ? 'Ye digest already notification queue me hai.'
            : 'Pehle real digest build karein.'
        );

        return;
      }

      const token = getToken();

      if (!token) {
        handleAuthFailure();
        return;
      }

      const confirmed = window.confirm(
        `Digest #${digestData.digestId} ke selected customers ko Notification Queue me bhejna hai?`
      );

      if (!confirmed) return;

      try {
        setQueueingDigest(true);
        setQueueStatusMessage('');
        setPageError('');

        const response = await fetch(
          `${window.location.origin}/api/control-center/notifications/queue-digest`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              digestId: Number(digestData.digestId),
            }),
          }
        );

        const data = await response
          .json()
          .catch(() => ({}));

        if (
          response.status === 401 ||
          response.status === 403
        ) {
          handleAuthFailure();
          return;
        }

        if (
          !response.ok ||
          !data?.success
        ) {
          throw new Error(
            data?.details ||
            data?.error ||
            'Notification queue nahi hua.'
          );
        }

        const result = data.result || {};

        setDigestData((prev) => ({
          ...prev,
          status:
            result.status ||
            'queued',
        }));

        const message =
          `${Number(result.queued || 0)} queued` +
          ` • ${Number(result.duplicates || 0)} duplicate` +
          ` • ${Number(result.pushDisabled || 0)} push off` +
          ` • ${Number(result.cooldownBlocked || 0)} cooldown` +
          ` • ${Number(result.fatigueBlocked || 0)} 7-day limit`;

        setQueueStatusMessage(message);

        await loadSmartDigestData();

        alert(
          data.message ||
          'Notifications queue ho gaye.'
        );

      } catch (error) {
        console.error(
          'Queue digest error:',
          error
        );

        setPageError(
          error.message ||
          'Notification queue nahi hua.'
        );

        alert(
          error.message ||
          'Notification queue nahi hua.'
        );

      } finally {
        setQueueingDigest(false);
      }
    };


  // ============================================================
  // CAMPAIGN STATUS CLASS
  // ============================================================

  const getCampaignStatusClass =
    (status) => {
      if (
        status === 'ready'
      ) {
        return 'ready';
      }

      if (
        status === 'scheduled' ||
        status === 'queued'
      ) {
        return 'scheduled';
      }

      if (
        status === 'completed'
      ) {
        return 'completed';
      }

      return 'inactive';
    };


  // ============================================================
  // JSX
  // ============================================================

  return (
    <div className="vccv-app">

      {sidebarOpen && (
        <button
          type="button"
          className="vccv-overlay"
          onClick={() =>
            setSidebarOpen(false)
          }
          aria-label="Close menu"
        />
      )}


      {/* ================= SIDEBAR ================= */}

      <aside
        className={`vccv-sidebar ${
          sidebarOpen
            ? 'vccv-sidebar-open'
            : ''
        }`}
      >

        <div className="vccv-brand-row">

          <div className="vccv-brand">

            <div className="vccv-logo">
              V
            </div>

            <div>
              <strong>
                VAKRA
              </strong>

              <span>
                CONTROL CENTER
              </span>
            </div>

          </div>


          <button
            type="button"
            className="vccv-close"
            onClick={() =>
              setSidebarOpen(false)
            }
          >
            <X size={20} />
          </button>

        </div>


        <div className="vccv-admin-card">

          <div className="vccv-admin-avatar">
            {String(
              admin?.name || 'A'
            )
              .charAt(0)
              .toUpperCase()}
          </div>

          <div>
            <strong>
              {admin?.name ||
                'Administrator'}
            </strong>

            <span>
              {admin?.role ||
                'admin'}
            </span>
          </div>

        </div>


        <nav className="vccv-nav">

          {menuItems.map(
            (item) => {
              const Icon =
                item.icon;

              return (
                <button
                  key={
                    item.label
                  }
                  type="button"
                  className={`vccv-nav-item ${
                    item.active
                      ? 'active'
                      : ''
                  }`}
                  onClick={() =>
                    handleMenuClick(
                      item
                    )
                  }
                >
                  <Icon
                    size={18}
                  />

                  <span>
                    {item.label}
                  </span>

                  {item.active && (
                    <i />
                  )}
                </button>
              );
            }
          )}

        </nav>


        <div className="vccv-sidebar-bottom">

          <div className="vccv-secure">

            <ShieldCheck
              size={19}
            />

            <div>
              <strong>
                Secure Admin
              </strong>

              <span>
                Protected session
              </span>
            </div>

          </div>


          <button
            type="button"
            className="vccv-logout"
            onClick={
              handleLogout
            }
          >
            <LogOut
              size={18}
            />

            Logout
          </button>

        </div>

      </aside>


      {/* ================= MAIN ================= */}

      <main className="vccv-main">

        <header className="vccv-topbar">

          <div className="vccv-top-left">

            <button
              type="button"
              className="vccv-mobile-menu"
              onClick={() =>
                setSidebarOpen(true)
              }
            >
              <Menu
                size={21}
              />
            </button>

            <div>
              <p>
                VAKRA CONTROL CENTER
              </p>

              <h1>
                Smart Offer Digest
              </h1>
            </div>

          </div>


          <div className="vccv-top-right">

            <button
              type="button"
              className="vccv-notification"
              onClick={() =>
                alert(
                  'Notifications page next step me connect karenge.'
                )
              }
            >
              <Bell
                size={19}
              />
            </button>


            <div className="vccv-top-profile">

              <div className="vccv-profile-avatar">
                {String(
                  admin?.name ||
                    'A'
                )
                  .charAt(0)
                  .toUpperCase()}
              </div>

              <div>
                <strong>
                  {admin?.name ||
                    'Admin'}
                </strong>

                <span>
                  {admin?.email ||
                    ''}
                </span>
              </div>

            </div>

          </div>

        </header>


        {/* ================= CONTENT ================= */}

        <div className="vccv-content vccsd-content">


          {/* ================= ERROR ================= */}

          {pageError && (
            <div
              style={{
                marginBottom:
                  '16px',

                padding:
                  '12px 15px',

                border:
                  '1px solid rgba(239,68,68,.25)',

                borderRadius:
                  '12px',

                background:
                  'rgba(239,68,68,.08)',

                color:
                  '#fca5a5',

                fontSize:
                  '12px',
              }}
            >
              {pageError}
            </div>
          )}


          {/* PAGE HEADING */}

          <section className="vccv-page-heading">

            <div>

              <span>
                <Sparkles
                  size={16}
                />

                Smart Recommendation Engine
              </span>

              <h2>
                Smart Offer Digest
              </h2>

              <p>
                Fresh location, order history,
                favourite vendor / food,
                notification eligibility aur
                live vendor offers ke basis par
                personalized audience aur
                offer digest manage karein.
              </p>

            </div>


            <button
              type="button"
              className="vccv-add-button"
              onClick={
                handleBuildDigest
              }
              disabled={
                buildingDigest ||
                loadingData
              }
            >

              <RefreshCw
                size={18}
                className={
                  buildingDigest ||
                  loadingData
                    ? 'vccsd-spin'
                    : ''
                }
              />

              {buildingDigest
                ? 'Building...'
                : loadingData
                ? 'Loading...'
                : 'Build Digest'}

            </button>

          </section>


          {/* ================= REAL STATS ================= */}

          <section className="vccv-stats">

            <article>

              <div className="vccv-stat-icon">
                <Sparkles
                  size={21}
                />
              </div>

              <div>
                <span>
                  Active Campaigns
                </span>

                <strong>
                  {totals.campaigns}
                </strong>

                <small>
                  Smart offer campaigns
                </small>
              </div>

            </article>


            <article
              role="button"
              tabIndex={0}
              onClick={() => toggleDetailPanel('eligible-customers')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  toggleDetailPanel('eligible-customers');
                }
              }}
              style={{ cursor: 'pointer' }}
            >

              <div className="vccv-stat-icon green">
                <Users
                  size={21}
                />
              </div>

              <div>
                <span>
                  Eligible Customers
                </span>

                <strong>
                  {totals
                    .eligibleCustomers}
                </strong>

                <small>
                  Click karke customers dekhein
                </small>
              </div>

            </article>


            <article
              role="button"
              tabIndex={0}
              onClick={() => toggleDetailPanel('matching-vendors')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  toggleDetailPanel('matching-vendors');
                }
              }}
              style={{ cursor: 'pointer' }}
            >

              <div className="vccv-stat-icon yellow">
                <Store
                  size={21}
                />
              </div>

              <div>
                <span>
                  Matching Vendors
                </span>

                <strong>
                  {totals
                    .matchingVendors}
                </strong>

                <small>
                  Click karke vendors dekhein
                </small>
              </div>

            </article>


            <article>

              <div className="vccv-stat-icon red">
                <Target
                  size={21}
                />
              </div>

              <div>
                <span>
                  Today's Batch
                </span>

                <strong>
                  {totals.todayBatch}
                </strong>

                <small>
                  Selected customers
                </small>
              </div>

            </article>

          </section>


          {/* ================= ELIGIBLE CUSTOMER DETAILS ================= */}

          {showEligibleCustomers && (
            <section className="vccsd-panel">
              <div className="vccsd-panel-heading">
                <div>
                  <span className="vccsd-eyebrow">ELIGIBLE CUSTOMERS</span>
                  <h3>Kaun Customers Eligible Hain</h3>
                  <p>
                    Personalized rule: active push + cooldown clear + fresh GPS par nearby
                    offers, ya stale GPS par customer ke previous / favourite vendors ke
                    relevant active offers.
                  </p>
                </div>

                <Users size={20} />
              </div>

              <div className="vccsd-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Area / Location</th>
                      <th>Order History</th>
                      <th>Favourite Signals</th>
                      <th>Matches</th>
                      <th>Best Score</th>
                      <th>Matched Vendor / Offer</th>
                    </tr>
                  </thead>

                  <tbody>
                    {eligibleCustomers.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '25px' }}>
                          Abhi koi eligible customer nahi mila.
                        </td>
                      </tr>
                    ) : (
                      eligibleCustomers.map((customer) => (
                        <tr key={customer.customerId}>
                          <td>
                            <strong>{customer.name || `Customer #${customer.customerId}`}</strong>
                            <span>Customer #{customer.customerId}</span>
                            <span style={{ display: 'block' }}>
                              {customer.mobile || 'Mobile not available'}
                            </span>
                          </td>

                          <td>
                            <strong>
                              {customer.area || customer.city || customer.defaultAddress || '-'}
                            </strong>
                            <span style={{ display: 'block' }}>
                              Location: {customer.locationFreshness || 'unknown'}
                              {customer.locationAgeHours === null ||
                              customer.locationAgeHours === undefined
                                ? ''
                                : ` • ${Number(customer.locationAgeHours).toFixed(1)}h old`}
                            </span>
                            <span style={{ display: 'block' }}>
                              Updated: {customer.locationUpdatedAt
                                ? formatDateTime(customer.locationUpdatedAt)
                                : '-'}
                            </span>
                          </td>

                          <td>
                            <strong>{Number(customer.totalOrders || 0)} orders</strong>
                            <span style={{ display: 'block' }}>
                              Last: {customer.lastOrderAt
                                ? formatDateTime(customer.lastOrderAt)
                                : 'No previous order'}
                            </span>
                          </td>

                          <td>
                            <span style={{ display: 'block' }}>
                              Favourite Vendor:{' '}
                              {customer.favoriteVendorId
                                ? `#${customer.favoriteVendorId}`
                                : '-'}
                            </span>

                            <span style={{ display: 'block' }}>
                              Food:{' '}
                              {(customer.favoriteFoods || []).length
                                ? (customer.favoriteFoods || [])
                                    .slice(0, 3)
                                    .map((item) => item.name || item.key || String(item))
                                    .join(', ')
                                : '-'}
                            </span>

                            <span style={{ display: 'block' }}>
                              Category:{' '}
                              {(customer.favoriteCategories || []).length
                                ? (customer.favoriteCategories || [])
                                    .slice(0, 3)
                                    .map((item) => item.name || item.key || String(item))
                                    .join(', ')
                                : '-'}
                            </span>
                          </td>

                          <td>{Number(customer.matchCount || 0)}</td>

                          <td>{Number(customer.bestMatchScore || 0).toFixed(2)}</td>

                          <td>
                            {(customer.matches || []).slice(0, 3).map((match) => (
                              <div
                                key={`${customer.customerId}-${match.rank}-${match.vendorId}`}
                                style={{ marginBottom: '9px' }}
                              >
                                <strong>{match.vendorName}</strong>

                                <span style={{ display: 'block' }}>
                                  {match.offer?.offerName || `Offer #${match.offer?.id || ''}`}
                                  {' • Score '}
                                  {Number(match.matchScore || 0).toFixed(2)}
                                </span>

                                <span style={{ display: 'block' }}>
                                  {match.distanceKm === null ||
                                  match.distanceKm === undefined
                                    ? 'History / usual vendor match'
                                    : `${Number(match.distanceKm).toFixed(2)} KM`}
                                  {match.signals?.favoriteVendorMatch
                                    ? ' • Favourite Vendor'
                                    : ''}
                                  {match.signals?.foodPreferenceMatch
                                    ? ' • Food Match'
                                    : ''}
                                  {match.signals?.recentVendorMatch
                                    ? ' • Recent Vendor'
                                    : ''}
                                </span>
                              </div>
                            ))}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}


          {/* ================= MATCHING VENDOR DETAILS ================= */}

          {showMatchingVendors && (
            <section className="vccsd-panel">
              <div className="vccsd-panel-heading">
                <div>
                  <span className="vccsd-eyebrow">MATCHING VENDORS</span>
                  <h3>Kaun Vendors Match Hue</h3>
                  <p>
                    Yahan real vendor, matching customers aur matched offers dikh rahe hain.
                  </p>
                </div>

                <Store size={20} />
              </div>

              <div className="vccsd-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th>Address</th>
                      <th>Customers</th>
                      <th>Offers</th>
                      <th>Nearest Customer</th>
                      <th>Best Score</th>
                      <th>Matched Offers</th>
                    </tr>
                  </thead>

                  <tbody>
                    {matchingVendors.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '25px' }}>
                          Abhi koi matching vendor nahi mila.
                        </td>
                      </tr>
                    ) : (
                      matchingVendors.map((vendor) => (
                        <tr key={vendor.vendorId}>
                          <td>
                            <strong>{vendor.vendorName}</strong>
                            <span>Vendor #{vendor.vendorId}</span>
                          </td>

                          <td>{vendor.address || '-'}</td>

                          <td>{Number(vendor.matchingCustomers || 0)}</td>

                          <td>{Number(vendor.matchingOffers || 0)}</td>

                          <td>
                            {vendor.nearestCustomerDistanceKm === null ||
                            vendor.nearestCustomerDistanceKm === undefined
                              ? '-'
                              : `${Number(vendor.nearestCustomerDistanceKm).toFixed(2)} KM`}
                          </td>

                          <td>{Number(vendor.bestMatchScore || 0).toFixed(2)}</td>

                          <td>
                            {(vendor.offers || []).slice(0, 4).map((offer) => (
                              <div key={offer.id} style={{ marginBottom: '7px' }}>
                                <strong>{offer.offerName}</strong>
                                <span style={{ display: 'block' }}>
                                  {String(offer.offerType || '').replaceAll('_', ' ')}
                                  {offer.liveNow ? ' • Live Now' : ''}
                                </span>
                              </div>
                            ))}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}


          {/* ================= LIVE VENDOR OFFERS ================= */}

          <section className="vccsd-panel">
            <button
              type="button"
              onClick={() => toggleDetailPanel('vendor-offers')}
              style={{
                width: '100%',
                background: 'transparent',
                border: '0',
                padding: 0,
                color: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <div className="vccsd-panel-heading">
                <div>
                  <span className="vccsd-eyebrow">LIVE VENDOR OFFERS</span>
                  <h3>Kaun Vendor Ne Kya Offer Rakha Hai</h3>
                  <p>
                    Active Vendors: {vendorOfferSummary.activeOfferVendors}
                    {' • '}Total Offers: {vendorOfferSummary.totalActiveOffers}
                    {' • '}Live Now: {vendorOfferSummary.liveNowOffers}
                    {' • '}{showVendorOffers ? 'Hide Details' : 'Click to View'}
                  </p>
                </div>

                <Sparkles size={20} />
              </div>
            </button>

            {showVendorOffers && (
              <div className="vccsd-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th>Address</th>
                      <th>Active Offers</th>
                      <th>Offer Details</th>
                    </tr>
                  </thead>

                  <tbody>
                    {vendorOffers.length === 0 ? (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center', padding: '25px' }}>
                          Abhi koi active vendor offer nahi mila.
                        </td>
                      </tr>
                    ) : (
                      vendorOffers.map((vendor) => (
                        <tr key={vendor.vendorId}>
                          <td>
                            <strong>{vendor.vendorName}</strong>
                            <span>Vendor #{vendor.vendorId}</span>
                          </td>

                          <td>{vendor.address || '-'}</td>

                          <td>
                            {Number(vendor.activeOfferCount || 0)}
                            <span style={{ display: 'block' }}>
                              Live now: {Number(vendor.liveNowCount || 0)}
                            </span>
                          </td>

                          <td>
                            {(vendor.activeOffers || []).map((offer) => (
                              <div
                                key={offer.id}
                                style={{
                                  marginBottom: '10px',
                                  paddingBottom: '10px',
                                  borderBottom: '1px solid rgba(255,255,255,.06)',
                                }}
                              >
                                <strong>{offer.offerName}</strong>

                                <span style={{ display: 'block' }}>
                                  Type: {String(offer.offerType || '').replaceAll('_', ' ')}
                                  {' • '}
                                  {offer.liveNow ? 'Live Now' : 'Active / not in current slot'}
                                </span>

                                {Number(offer.actualPrice || 0) > 0 && (
                                  <span style={{ display: 'block' }}>
                                    ₹{Number(offer.actualPrice || 0)} → ₹{Number(offer.offerPrice || 0)}
                                    {Number(offer.saving || 0) > 0
                                      ? ` • Save ₹${Number(offer.saving || 0)}`
                                      : ''}
                                  </span>
                                )}

                                {Number(offer.discountValue || 0) > 0 && (
                                  <span style={{ display: 'block' }}>
                                    Discount: {Number(offer.discountValue || 0)}
                                    {offer.discountType === 'percent' ? '%' : ' ₹'}
                                    {Number(offer.minBillAmount || 0) > 0
                                      ? ` • Min Bill ₹${Number(offer.minBillAmount || 0)}`
                                      : ''}
                                  </span>
                                )}

                                {(offer.startTime || offer.endTime) && (
                                  <span style={{ display: 'block' }}>
                                    Time: {offer.startTime || '-'} - {offer.endTime || '-'}
                                  </span>
                                )}
                              </div>
                            ))}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>


          {/* ================= QR ORDER NETWORK ================= */}

          <section className="vccsd-panel">
            <button
              type="button"
              onClick={() => toggleDetailPanel('qr-analytics')}
              style={{
                width: '100%',
                background: 'transparent',
                border: '0',
                padding: 0,
                color: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <div className="vccsd-panel-heading">
                <div>
                  <span className="vccsd-eyebrow">QR ORDER NETWORK</span>
                  <h3>Overall QR Orders & Customers</h3>
                  <p>
                    Total QR Orders: {qrAnalytics.summary.totalQrOrders}
                    {' • '}Unique Customers: {qrAnalytics.summary.uniqueIdentifiedCustomers}
                    {' • '}QR Vendors: {qrAnalytics.summary.qrVendors}
                    {' • '}{showQrAnalytics ? 'Hide Details' : 'Click to View'}
                  </p>
                </div>

                <Target size={20} />
              </div>
            </button>

            {showQrAnalytics && (
              <>
                <div className="vccsd-digest-grid">
                  <div>
                    <span>Total QR Orders</span>
                    <strong>{qrAnalytics.summary.totalQrOrders}</strong>
                  </div>

                  <div>
                    <span>Unique Identified Customers</span>
                    <strong>{qrAnalytics.summary.uniqueIdentifiedCustomers}</strong>
                  </div>

                  <div>
                    <span>Anonymous QR Orders</span>
                    <strong>{qrAnalytics.summary.anonymousQrOrders}</strong>
                  </div>

                  <div>
                    <span>QR Vendors</span>
                    <strong>{qrAnalytics.summary.qrVendors}</strong>
                  </div>

                  <div>
                    <span>Today's QR Orders</span>
                    <strong>{qrAnalytics.summary.todayQrOrders}</strong>
                  </div>
                </div>

                <div className="vccsd-table-wrap" style={{ marginTop: '18px' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th>QR Orders</th>
                        <th>Unique Customers</th>
                        <th>Today's Orders</th>
                        <th>QR Sales</th>
                      </tr>
                    </thead>

                    <tbody>
                      {qrAnalytics.vendors.length === 0 ? (
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', padding: '25px' }}>
                            Abhi QR order data available nahi hai.
                          </td>
                        </tr>
                      ) : (
                        qrAnalytics.vendors.map((vendor) => (
                          <tr key={vendor.vendorId}>
                            <td>
                              <strong>{vendor.vendorName}</strong>
                              <span>{vendor.address || `Vendor #${vendor.vendorId}`}</span>
                            </td>

                            <td>{Number(vendor.qrOrders || 0)}</td>
                            <td>{Number(vendor.uniqueCustomers || 0)}</td>
                            <td>{Number(vendor.todayOrders || 0)}</td>
                            <td>₹{Number(vendor.qrSales || 0).toFixed(2)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="vccsd-panel-heading" style={{ marginTop: '22px' }}>
                  <div>
                    <span className="vccsd-eyebrow">RECENT QR ORDERS</span>
                    <h3>Latest QR Orders</h3>
                  </div>
                </div>

                <div className="vccsd-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Vendor</th>
                        <th>Customer</th>
                        <th>Mode / Table</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Time</th>
                      </tr>
                    </thead>

                    <tbody>
                      {qrAnalytics.recentOrders.length === 0 ? (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', padding: '25px' }}>
                            Recent QR orders nahi mile.
                          </td>
                        </tr>
                      ) : (
                        qrAnalytics.recentOrders.slice(0, 25).map((order) => (
                          <tr key={order.orderId}>
                            <td>
                              <strong>#{order.orderId}</strong>
                            </td>

                            <td>{order.vendorName}</td>

                            <td>
                              <strong>
                                {order.customerName ||
                                  (order.identifiedCustomer ? 'Identified Customer' : 'Anonymous')}
                              </strong>
                              <span style={{ display: 'block' }}>
                                {order.customerMobile || 'Mobile not provided'}
                              </span>
                            </td>

                            <td>
                              {order.orderMode || '-'}
                              {order.tableNumber ? ` / ${order.tableNumber}` : ''}
                            </td>

                            <td>₹{Number(order.totalPrice || 0).toFixed(2)}</td>
                            <td>{order.status || '-'}</td>
                            <td>{formatDateTime(order.createdAt)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>


          {/* ================= PLATFORM CUSTOMER ANALYTICS ================= */}

          <section className="vccsd-panel">
            <button
              type="button"
              onClick={() => toggleDetailPanel('platform-analytics')}
              style={{
                width: '100%',
                background: 'transparent',
                border: '0',
                padding: 0,
                color: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <div className="vccsd-panel-heading">
                <div>
                  <span className="vccsd-eyebrow">
                    PLATFORM CUSTOMER ANALYTICS
                  </span>

                  <h3>
                    Overall Customers & Orders
                  </h3>

                  <p>
                    Total Customers: {platformAnalytics.summary.totalCustomers}
                    {' • '}New Today: {platformAnalytics.summary.newCustomersToday}
                    {' • '}Total Orders: {platformAnalytics.summary.totalOrders}
                    {' • '}
                    {showPlatformAnalytics
                      ? 'Hide Details'
                      : 'Click to View'}
                  </p>
                </div>

                <Users size={20} />
              </div>
            </button>

            {showPlatformAnalytics && (
              <>
                <div className="vccsd-digest-grid">
                  <div>
                    <span>Total Unique Customers</span>
                    <strong>{platformAnalytics.summary.totalCustomers}</strong>
                  </div>

                  <div>
                    <span>New Customers Today</span>
                    <strong>{platformAnalytics.summary.newCustomersToday}</strong>
                  </div>

                  <div>
                    <span>New This Week</span>
                    <strong>{platformAnalytics.summary.newCustomersThisWeek}</strong>
                  </div>

                  <div>
                    <span>New This Month</span>
                    <strong>{platformAnalytics.summary.newCustomersThisMonth}</strong>
                  </div>

                  <div>
                    <span>Repeat Customers</span>
                    <strong>{platformAnalytics.summary.repeatCustomers}</strong>
                  </div>

                  <div>
                    <span>Total Orders</span>
                    <strong>{platformAnalytics.summary.totalOrders}</strong>
                  </div>

                  <div>
                    <span>Orders Today</span>
                    <strong>{platformAnalytics.summary.ordersToday}</strong>
                  </div>

                  <div>
                    <span>Anonymous Orders</span>
                    <strong>{platformAnalytics.summary.anonymousOrders}</strong>
                  </div>
                </div>


                <div className="vccsd-panel-heading" style={{ marginTop: '22px' }}>
                  <div>
                    <span className="vccsd-eyebrow">ORDER SOURCE</span>
                    <h3>Order Kahan Se Aaya</h3>
                  </div>
                </div>

                <div className="vccsd-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th>Orders</th>
                        <th>Unique Customers</th>
                        <th>Sales</th>
                      </tr>
                    </thead>

                    <tbody>
                      {platformAnalytics.sources.length === 0 ? (
                        <tr>
                          <td
                            colSpan="4"
                            style={{ textAlign: 'center', padding: '25px' }}
                          >
                            Order source data available nahi hai.
                          </td>
                        </tr>
                      ) : (
                        platformAnalytics.sources.map((source) => (
                          <tr key={source.source}>
                            <td>
                              <strong>
                                {source.source === 'food-radar'
                                  ? 'Food Radar'
                                  : source.source === 'admin'
                                  ? 'Counter / Admin'
                                  : String(source.source || 'Other').toUpperCase()}
                              </strong>
                            </td>

                            <td>{Number(source.orders || 0)}</td>
                            <td>{Number(source.uniqueCustomers || 0)}</td>
                            <td>₹{Number(source.sales || 0).toFixed(2)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>


                <div className="vccsd-panel-heading" style={{ marginTop: '22px' }}>
                  <div>
                    <span className="vccsd-eyebrow">ORDER MODE</span>
                    <h3>Dine-in / Takeaway / Delivery</h3>
                  </div>
                </div>

                <div className="vccsd-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Order Mode</th>
                        <th>Orders</th>
                        <th>Unique Customers</th>
                        <th>Sales</th>
                      </tr>
                    </thead>

                    <tbody>
                      {platformAnalytics.orderModes.length === 0 ? (
                        <tr>
                          <td
                            colSpan="4"
                            style={{ textAlign: 'center', padding: '25px' }}
                          >
                            Order mode data available nahi hai.
                          </td>
                        </tr>
                      ) : (
                        platformAnalytics.orderModes.map((mode) => (
                          <tr key={mode.mode}>
                            <td>
                              <strong>
                                {String(mode.mode || 'other')
                                  .replaceAll('_', ' ')
                                  .toUpperCase()}
                              </strong>
                            </td>

                            <td>{Number(mode.orders || 0)}</td>
                            <td>{Number(mode.uniqueCustomers || 0)}</td>
                            <td>₹{Number(mode.sales || 0).toFixed(2)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>


                <div className="vccsd-panel-heading" style={{ marginTop: '22px' }}>
                  <div>
                    <span className="vccsd-eyebrow">PAYMENT</span>
                    <h3>Payment Status Breakdown</h3>
                  </div>
                </div>

                <div className="vccsd-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Orders</th>
                        <th>Sales</th>
                      </tr>
                    </thead>

                    <tbody>
                      {platformAnalytics.payments.length === 0 ? (
                        <tr>
                          <td
                            colSpan="3"
                            style={{ textAlign: 'center', padding: '25px' }}
                          >
                            Payment data available nahi hai.
                          </td>
                        </tr>
                      ) : (
                        platformAnalytics.payments.map((payment) => (
                          <tr key={payment.status}>
                            <td>
                              <strong>
                                {String(payment.status || 'unknown').toUpperCase()}
                              </strong>
                            </td>

                            <td>{Number(payment.orders || 0)}</td>
                            <td>₹{Number(payment.sales || 0).toFixed(2)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>


                <div className="vccsd-panel-heading" style={{ marginTop: '22px' }}>
                  <div>
                    <span className="vccsd-eyebrow">VENDOR BREAKDOWN</span>
                    <h3>Vendor-wise Customers & Orders</h3>
                  </div>
                </div>

                <div className="vccsd-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th>Total Orders</th>
                        <th>Customers</th>
                        <th>Today</th>
                        <th>QR</th>
                        <th>Food Radar</th>
                        <th>Counter</th>
                        <th>Sales</th>
                      </tr>
                    </thead>

                    <tbody>
                      {platformAnalytics.vendors.length === 0 ? (
                        <tr>
                          <td
                            colSpan="8"
                            style={{ textAlign: 'center', padding: '25px' }}
                          >
                            Vendor order data available nahi hai.
                          </td>
                        </tr>
                      ) : (
                        platformAnalytics.vendors.map((vendor) => (
                          <tr key={vendor.vendorId}>
                            <td>
                              <strong>{vendor.vendorName}</strong>
                              <span>
                                {vendor.address || `Vendor #${vendor.vendorId}`}
                              </span>
                            </td>

                            <td>{Number(vendor.totalOrders || 0)}</td>
                            <td>{Number(vendor.uniqueCustomers || 0)}</td>
                            <td>{Number(vendor.todayOrders || 0)}</td>
                            <td>{Number(vendor.qrOrders || 0)}</td>
                            <td>{Number(vendor.foodRadarOrders || 0)}</td>
                            <td>{Number(vendor.counterOrders || 0)}</td>
                            <td>₹{Number(vendor.totalSales || 0).toFixed(2)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>


                <div className="vccsd-panel-heading" style={{ marginTop: '22px' }}>
                  <div>
                    <span className="vccsd-eyebrow">NEW NUMBERS</span>
                    <h3>Recent New Customer Numbers</h3>
                    <p>
                      Mobile ka first-ever order jis date par aaya, wahi us customer
                      ka first order maana gaya hai.
                    </p>
                  </div>
                </div>

                <div className="vccsd-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Mobile</th>
                        <th>First Order</th>
                        <th>Last Order</th>
                        <th>Total Orders</th>
                        <th>Type</th>
                      </tr>
                    </thead>

                    <tbody>
                      {platformAnalytics.recentNewCustomers.length === 0 ? (
                        <tr>
                          <td
                            colSpan="5"
                            style={{ textAlign: 'center', padding: '25px' }}
                          >
                            Customer mobile data available nahi hai.
                          </td>
                        </tr>
                      ) : (
                        platformAnalytics.recentNewCustomers
                          .slice(0, 50)
                          .map((customer) => (
                            <tr
                              key={`${customer.mobile}-${customer.firstOrderAt}`}
                            >
                              <td>
                                <strong>{customer.mobile}</strong>
                              </td>

                              <td>{formatDateTime(customer.firstOrderAt)}</td>
                              <td>{formatDateTime(customer.lastOrderAt)}</td>
                              <td>{Number(customer.totalOrders || 0)}</td>
                              <td>
                                {customer.customerType === 'repeat'
                                  ? 'REPEAT'
                                  : 'NEW'}
                              </td>
                            </tr>
                          ))
                      )}
                    </tbody>
                  </table>
                </div>


                <div className="vccsd-panel-heading" style={{ marginTop: '22px' }}>
                  <div>
                    <span className="vccsd-eyebrow">RECENT ORDERS</span>
                    <h3>Latest Platform Orders</h3>
                  </div>
                </div>

                <div className="vccsd-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Vendor</th>
                        <th>Customer</th>
                        <th>Source</th>
                        <th>Mode</th>
                        <th>Total</th>
                        <th>Payment</th>
                        <th>Status</th>
                        <th>Time</th>
                      </tr>
                    </thead>

                    <tbody>
                      {platformAnalytics.recentOrders.length === 0 ? (
                        <tr>
                          <td
                            colSpan="9"
                            style={{ textAlign: 'center', padding: '25px' }}
                          >
                            Recent platform orders nahi mile.
                          </td>
                        </tr>
                      ) : (
                        platformAnalytics.recentOrders
                          .slice(0, 50)
                          .map((order) => (
                            <tr key={order.orderId}>
                              <td>
                                <strong>#{order.orderId}</strong>
                              </td>

                              <td>{order.vendorName}</td>

                              <td>
                                <strong>
                                  {order.customerName ||
                                    (order.identifiedCustomer
                                      ? 'Identified Customer'
                                      : 'Anonymous')}
                                </strong>

                                <span>
                                  {order.customerMobile ||
                                    'Mobile not provided'}
                                </span>
                              </td>

                              <td>{order.source}</td>
                              <td>{order.orderMode}</td>
                              <td>₹{Number(order.totalPrice || 0).toFixed(2)}</td>
                              <td>{order.paymentStatus}</td>
                              <td>{order.status}</td>
                              <td>{formatDateTime(order.createdAt)}</td>
                            </tr>
                          ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>


          {/* ================= TODAY DIGEST ================= */}

          <section className="vccsd-panel vccsd-current-digest">

            <div className="vccsd-panel-heading">

              <div>

                <span className="vccsd-eyebrow">
                  LATEST DIGEST
                </span>

                <h3>
                  {digestData
                    .campaignName}
                </h3>

                <p>
                  <MapPin
                    size={14}
                  />

                  {digestData
                    .microZone}

                  <span>
                    •
                  </span>

                  {digestData
                    .radius}

                  <span>
                    •
                  </span>

                  <Clock3
                    size={14}
                  />

                  {digestData
                    .createdAt
                    ? formatDateTime(
                        digestData
                          .createdAt
                      )
                    : 'Not built yet'}
                </p>

              </div>


              <span
                className={`vccsd-digest-status ${
                  digestData.status
                }`}
              >
                <i />

                {digestData
                  .status}
              </span>

            </div>


            <div className="vccsd-digest-grid">

              <div>
                <span>
                  Eligible Customers
                </span>

                <strong>
                  {digestData
                    .eligibleCustomers}
                </strong>
              </div>


              <div>
                <span>
                  Today's Batch
                </span>

                <strong>
                  {digestData
                    .selectedCustomers}
                </strong>
              </div>


              <div>
                <span>
                  Matching Vendors
                </span>

                <strong>
                  {digestData
                    .matchingVendors}
                </strong>
              </div>


              <div>
                <span>
                  Selected Offers
                </span>

                <strong>
                  {digestData
                    .selectedOffers}
                </strong>
              </div>

            </div>


            <div className="vccsd-digest-actions">

              <button
                type="button"
                className="vccsd-secondary-button"
                disabled={
                  !digestData
                    .digestId
                }
                onClick={() =>
                  alert(
                    digestData
                      .digestId
                      ? `Digest #${digestData.digestId} preview next step me open karenge.`
                      : 'Abhi koi digest build nahi hua.'
                  )
                }
              >
                <Eye
                  size={17}
                />

                Preview Digest
              </button>


              <button
                type="button"
                className="vccsd-send-button"
                onClick={
                  handleSendToNotificationQueue
                }
                disabled={
                  !digestData.digestId ||
                  queueingDigest ||
                  digestData.status !== 'ready'
                }
              >
                {queueingDigest ? (
                  <RefreshCw
                    size={17}
                    className="vccsd-spin"
                  />
                ) : (
                  <Send size={17} />
                )}

                {queueingDigest
                  ? 'Queueing...'
                  : digestData.status === 'queued'
                  ? 'Already Queued'
                  : digestData.status === 'scheduled'
                  ? 'Scheduled'
                  : 'Send to Notification Queue'}
              </button>

            </div>

            {queueStatusMessage && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  background: 'rgba(34,197,94,.08)',
                  border: '1px solid rgba(34,197,94,.22)',
                  fontSize: '12px',
                }}
              >
                {queueStatusMessage}
              </div>
            )}

          </section>


          {/* ================= AUDIENCE + RULES ================= */}

          <div className="vccsd-two-column">


            <section className="vccsd-panel">

              <div className="vccsd-panel-heading">

                <div>

                  <span className="vccsd-eyebrow">
                    AUDIENCE
                  </span>

                  <h3>
                    Personalized Audience Matching
                  </h3>

                </div>

                <Users
                  size={20}
                />

              </div>


              <div className="vccsd-audience-list">


                <div>
                  <span>
                    Total Customer Pool
                  </span>

                  <strong>
                    {audience
                      .totalPool}
                  </strong>
                </div>


                <div className="success">
                  <span>
                    Eligible
                  </span>

                  <strong>
                    {audience
                      .eligible}
                  </strong>
                </div>


                <div className="warning">
                  <span>
                    Cooldown Blocked
                  </span>

                  <strong>
                    {audience
                      .cooldownBlocked}
                  </strong>
                </div>


                <div className="muted">
                  <span>
                    Push Disabled
                  </span>

                  <strong>
                    {audience
                      .pushDisabled}
                  </strong>
                </div>


                <div className="danger">
                  <span>
                    No Personalized Match
                  </span>

                  <strong>
                    {audience
                      .noMatch}
                  </strong>
                </div>

              </div>

            </section>


            <section className="vccsd-panel">

              <div className="vccsd-panel-heading">

                <div>

                  <span className="vccsd-eyebrow">
                    MATCHING RULES
                  </span>

                  <h3>
                    Current Digest Rules
                  </h3>

                </div>

                <ShieldCheck
                  size={20}
                />

              </div>


              <div className="vccsd-rule-list">

                <div>
                  <CircleCheck size={16} />
                  <span>
                    Maximum 10 matching offers aur maximum 2 offers per vendor
                  </span>
                </div>

                <div>
                  <CircleCheck size={16} />
                  <span>
                    Fresh GPS: 3 hours strong signal, 6 hours tak nearby matching usable
                  </span>
                </div>

                <div>
                  <CircleCheck size={16} />
                  <span>
                    Stale GPS par random nearby push nahi; previous / favourite vendor history use hogi
                  </span>
                </div>

                <div>
                  <CircleCheck size={16} />
                  <span>
                    Last 180 days order history se favourite vendor, food aur category signals
                  </span>
                </div>

                <div>
                  <CircleCheck size={16} />
                  <span>
                    Active FCM token + granted notification permission + push enabled required
                  </span>
                </div>

                <div>
                  <CircleCheck size={16} />
                  <span>
                    Promotional digest cooldown 72 hours aur maximum 3 notifications per 7 days
                  </span>
                </div>

                <div>
                  <CircleCheck size={16} />
                  <span>
                    Active fastfood offers only; time-slot offer current timing ke according
                  </span>
                </div>

                <div>
                  <CircleAlert size={16} />
                  <span>
                    Send to Notification Queue button actual notification backend connect hone tak sirf ready digest validate karega
                  </span>
                </div>

              </div>

            </section>

          </div>


          {/* ================= REAL CAMPAIGNS ================= */}

          <section className="vccsd-panel">

            <div className="vccsd-panel-heading">

              <div>

                <span className="vccsd-eyebrow">
                  CAMPAIGNS
                </span>

                <h3>
                  Digest Campaigns
                </h3>

              </div>

              <Sparkles
                size={20}
              />

            </div>


            <div className="vccsd-table-wrap">

              <table>

                <thead>
                  <tr>
                    <th>
                      Campaign
                    </th>

                    <th>
                      Area
                    </th>

                    <th>
                      Created
                    </th>

                    <th>
                      Batch
                    </th>

                    <th>
                      Vendors
                    </th>

                    <th>
                      Status
                    </th>
                  </tr>
                </thead>


                <tbody>

                  {campaigns.length ===
                  0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        style={{
                          textAlign:
                            'center',
                          padding:
                            '25px',
                        }}
                      >
                        Abhi koi real
                        digest campaign
                        build nahi hua.
                      </td>
                    </tr>
                  ) : (

                    campaigns.map(
                      (
                        campaign
                      ) => (

                        <tr
                          key={
                            campaign.id
                          }
                        >

                          <td>
                            <strong>
                              {campaign
                                .name}
                            </strong>

                            <span>
                              Digest #
                              {campaign.id}
                            </span>
                          </td>


                          <td>
                            {campaign
                              .area ||
                              'All Areas'}
                          </td>


                          <td>
                            {formatDateTime(
                              campaign
                                .createdAt
                            )}
                          </td>


                          <td>
                            {Number(
                              campaign
                                .batch ||
                              0
                            )}
                          </td>


                          <td>
                            {Number(
                              campaign
                                .vendors ||
                              0
                            )}
                          </td>


                          <td>

                            <span
                              className={`vccsd-campaign-status ${getCampaignStatusClass(
                                campaign
                                  .status
                              )}`}
                            >
                              <i />

                              {campaign
                                .status}
                            </span>

                          </td>

                        </tr>
                      )
                    )
                  )}

                </tbody>

              </table>

            </div>

          </section>


          {/* ================= REAL VENDOR EXPOSURE ================= */}

          <section className="vccsd-panel">

            <div className="vccsd-panel-heading">

              <div>

                <span className="vccsd-eyebrow">
                  FAIRNESS
                </span>

                <h3>
                  Vendor Exposure
                </h3>

                <p>
                  Real digest matching
                  ke basis par vendor
                  exposure yahan show
                  hoga.
                </p>

              </div>

              <TrendingUp
                size={20}
              />

            </div>


            <div className="vccsd-table-wrap">

              <table>

                <thead>
                  <tr>
                    <th>
                      Vendor
                    </th>

                    <th>
                      Matches
                    </th>

                    <th>
                      Shown
                    </th>

                    <th>
                      Clicks
                    </th>

                    <th>
                      Orders
                    </th>

                    <th>
                      Exposure
                    </th>
                  </tr>
                </thead>


                <tbody>

                  {vendorExposure.length ===
                  0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        style={{
                          textAlign:
                            'center',

                          padding:
                            '25px',
                        }}
                      >
                        Vendor exposure
                        data abhi available
                        nahi hai. Pehle
                        digest build karein.
                      </td>
                    </tr>
                  ) : (

                    vendorExposure.map(
                      (
                        vendor
                      ) => (

                        <tr
                          key={
                            vendor
                              .vendorId
                          }
                        >

                          <td>

                            <div className="vccsd-vendor-name">

                              <div>
                                {String(
                                  vendor
                                    .vendor ||
                                    'V'
                                )
                                  .charAt(
                                    0
                                  )
                                  .toUpperCase()}
                              </div>

                              <strong>
                                {vendor
                                  .vendor}
                              </strong>

                            </div>

                          </td>


                          <td>
                            {Number(
                              vendor
                                .matches ||
                              0
                            )}
                          </td>


                          <td>
                            {Number(
                              vendor
                                .shown ||
                              0
                            )}
                          </td>


                          <td>
                            {Number(
                              vendor
                                .clicks ||
                              0
                            )}
                          </td>


                          <td>
                            {Number(
                              vendor
                                .orders ||
                              0
                            )}
                          </td>


                          <td>

                            <span
                              className={`vccsd-exposure ${
                                vendor
                                  .exposure ||
                                'normal'
                              }`}
                            >
                              {vendor
                                .exposure ===
                              'low'
                                ? 'Under-exposed'
                                : 'Normal'}
                            </span>

                          </td>

                        </tr>
                      )
                    )
                  )}

                </tbody>

              </table>

            </div>

          </section>

        </div>

      </main>

    </div>
  );
};


export default ControlCenterSmartDigest;
